import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { distinctBy, getTreatmentCodeByCountry, isEmptyValue, isFixedPrice } from "@lyfegen-platform/utils";
import { InputAdornment } from "@material-ui/core";
import { useFieldValue } from "formik";
import { useRecoilValue } from "recoil";
import * as Yup from "yup";
import { useDiscountRate } from "../hooks/use-discount-rate";
import { ControlState } from "../models/flexible/control-state";
import { FlexControlProps } from "../models/flexible/flex-field-controller";
import { BaseAgreement, Decision, PriceCategory, PriceType, Treatment } from "../models/generated/graphql";
import { flexibleFormRelatedAgreements } from "../store/flexible";
import { maxBy } from "../utils/max-by";
import { renderRequiredMessage } from "../utils/render-required-message";
import { GeneralFieldController } from "./general-field-controller";

export class FixedFeeAmountFieldController extends GeneralFieldController {
	override useControlState(path: string): ControlState {
		const { basePriceValue } = this.getValues(path);
		const [decisionTherapy] = useFieldValue<Decision>(AttributeIdentifiers.DecisionTherapy);
		return decisionTherapy == null || isEmptyValue(basePriceValue) || !isFixedPrice(basePriceValue) ? "hidden" : "enabled";
	}

	override useControlProps(path: string): FlexControlProps {
		const relatedAgreementsState = useRecoilValue(flexibleFormRelatedAgreements);
		const agreement = maxBy(relatedAgreementsState?.data || [], ({ agreement }) => agreement.pushedOn)?.agreement;
		const splittedPath = this.path.split(".");
		const index = +(path.split(".").find((e) => Number.isInteger(+e)) || 0);
		const setDiscountRate = useDiscountRate({
			index: index,
			sectionPath: splittedPath[splittedPath.length - 2],
			fieldPath: splittedPath[splittedPath.length - 1],
			childIndexes: this.parent.context.childBrandIndexes?.[index] || [],
		});

		return {
			currency: agreement?.currency?.Code,
			InputLabelProps: {
				shrink: true,
			},
			InputProps: {
				endAdornment: <InputAdornment position="end">{agreement?.currency?.Code}</InputAdornment>,
				inputProps: {
					onBlur: () => {
						if (this.parent.context.uniqueBrandIndexes?.includes(index) && this.parent.context.isEditable) {
							setDiscountRate();
						}
					},
				},
			},
		};
	}

	getValues(path: string): { basePriceValue: PriceType; pack: Treatment } {
		const [value] = useFieldValue(this.parent.path);
		const index = +(path.split(".").find((e) => Number.isInteger(+e)) || 0);

		return {
			basePriceValue: value[index][AttributeIdentifiers.BasePrice] as PriceType,
			pack: value[index][AttributeIdentifiers.PackReference] as Treatment,
		};
	}

	override createValidationRule(_initialData: unknown, translation: (key: string, params?: any) => string): Yup.AnySchema {
		return Yup.number()
			.nullable()
			.when(AttributeIdentifiers.BasePrice, (basePrice, schema) =>
				isFixedPrice(basePrice) ? schema.required(renderRequiredMessage(this.schema.attribute.identifier, translation)) : schema
			)
			.when(["$relatedAgreementsState", AttributeIdentifiers.PackReference, AttributeIdentifiers.BasePrice], ((
				relatedAgreementsState: { data?: Array<{ agreement: BaseAgreement }> },
				treatment: Treatment,
				basePrice: PriceType,
				schema: Yup.NumberSchema
			) => {
				const treatments = distinctBy(
					relatedAgreementsState?.data
						?.flatMap(({ agreement }) => agreement.treatmentPricesConnection.edges)
						.map((edge) => edge.node) || [],
					(treatment) => getTreatmentCodeByCountry(treatment) || ""
				);
				const priceCategory = basePrice === PriceType.ExFactoryPrice ? PriceCategory.Pexf : PriceCategory.Ppub;
				const latestPrice = maxBy(
					treatments
						.find((t) => getTreatmentCodeByCountry(treatment) === getTreatmentCodeByCountry(t))
						?.prices.filter((price) => price.priceType === priceCategory) || [],
					(t) => t.ValidFromDate
				);

				return latestPrice ? schema.max(+latestPrice.Price!, (params: any) => translation("less-then-or-equal", params)) : schema;
			}) as any);
	}
}
